import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Avatar,
  Dialog,
  GameChooser,
  Header,
  Icon,
  Rules,
  syncThemeColor,
} from "./components";
import { games, gameById, type GameInfo } from "./lib/catalogue";
import {
  api,
  forgetSeat,
  listSeats,
  readSeat,
  saveSeat,
  ensureSeatStorage,
  useRoom,
  type SeatCredentials,
  type Action,
  type RoomView,
} from "./lib/client";
import {
  announcements,
  lobbyStatus,
  nameList,
  pauseCopy,
  renamedOnJoin,
} from "./lib/room-copy";
import { Settings } from "./Settings";
import { Play } from "./Play";
import { editorSettings } from "./lib/settings-model";
import {
  cleanName,
  getSavedName,
  NAME_MAX,
  onNameChange,
  saveName,
} from "./lib/profile";
import { SwitchRow, TextField, ToastRegion, useToasts } from "./ui";
import { practicePath } from "./lib/practice";

/** Practice rounds (real engines + tutorial packs) load only on `/learn/…`. */
const Tutorial = lazy(() => import("./tutorial"));

type Obj = Record<string, any>;
const defaults = (g: GameInfo) => ({
  timerMode: "relaxed",
  clueSeconds: 45,
  decisionSeconds: 60,
  turnSeconds: 60,
  discussionSeconds: 120,
  ...g.defaults,
});
function navigate(path: string) {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0 });
}
function useSavedName() {
  const [name, setName] = useState(getSavedName);
  useEffect(() => onNameChange(setName), []);
  return name;
}
export default function App() {
  const [path, setPath] = useState(() => location.pathname);
  // `here`: opened from the catalogue, so its practice button stays in this tab
  // (in a room it opens a new tab and the room is untouched).
  const [rules, setRules] = useState<{ game: GameInfo; here: boolean } | null>(null);
  const savedName = useSavedName();
  const [nameSheet, setNameSheet] = useState(false);
  const showRules = (game: GameInfo, here = false) => {
    window.dispatchEvent(new Event("after-hours:cover"));
    setRules({ game, here });
  };
  const [create, setCreate] = useState<GameInfo | null>(null);
  useEffect(() => {
    const pop = () => setPath(location.pathname);
    addEventListener("popstate", pop);
    return () => removeEventListener("popstate", pop);
  }, []);
  const match = path.match(/^\/r\/([A-Z2-9]{6})(\/display)?\/?$/i);
  const learn = path.match(/^\/learn\/([a-z-]+)\/?$/);
  const home = () => navigate("/");
  const changeName = () => setNameSheet(true);
  // "Play for real" from a practice round lands on the catalogue with Create open.
  useEffect(() => {
    if (match || learn || !savedName || typeof location.search !== "string") return;
    const wanted = new URLSearchParams(location.search).get("create");
    const game = games.find((g) => g.id === wanted);
    if (!wanted) return;
    history.replaceState({}, "", "/");
    if (game) setCreate(game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, savedName]);
  return (
    <>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      {learn ? (
        <Suspense fallback={<PracticeLoading onHome={home} />}>
          <Tutorial
            key={learn[1]}
            gameId={learn[1]}
            savedName={savedName}
            onHome={home}
            onPlayForReal={(id) => navigate(`/?create=${id}`)}
          />
        </Suspense>
      ) : match ? (
        <Room
          key={match[1].toUpperCase() + Boolean(match[2])}
          code={match[1].toUpperCase()}
          isDisplay={Boolean(match[2])}
          home={home}
          onRules={showRules}
          savedName={savedName}
          onChangeName={changeName}
        />
      ) : savedName ? (
        <>
          <Header onHome={home}>
            <button
              className="name-chip"
              onClick={changeName}
              aria-label={`Playing as ${savedName}. Change your name`}
            >
              <Avatar name={savedName} index={0} size={28} />
              <span className="name-chip-text">{savedName}</span>
            </button>
          </Header>
          <Catalogue onPlay={setCreate} onRules={(g) => showRules(g, true)} />
        </>
      ) : (
        <Welcome onHome={home} />
      )}
      {rules ? (
        <Rules
          game={rules.game}
          onClose={() => setRules(null)}
          onPractice={
            rules.here
              ? () => {
                  setRules(null);
                  navigate(practicePath(rules.game.id));
                }
              : undefined
          }
        />
      ) : null}
      {create ? (
        <Create
          game={create}
          savedName={savedName}
          onChangeName={changeName}
          onClose={() => setCreate(null)}
          onCreated={(credentials) => {
            saveSeat(credentials.code, credentials);
            setCreate(null);
            navigate(`/r/${credentials.code}`);
          }}
        />
      ) : null}
      {nameSheet ? (
        <NameSheet
          current={savedName ?? ""}
          onClose={() => setNameSheet(false)}
        />
      ) : null}
    </>
  );
}

function PracticeLoading({ onHome }: { onHome: () => void }) {
  return (
    <>
      <Header onHome={onHome} />
      <main id="main" className="learn-page">
        <div className="center-card status-card">
          <span className="loading-emblem" aria-hidden="true">
            ✳
          </span>
          <p className="muted" role="status">
            Setting the table…
          </p>
        </div>
      </main>
    </>
  );
}

function CoverFan() {
  return (
    <div className="cover-fan" aria-hidden="true">
      <span className="art secret-hitler" />
      <span className="art mafia" />
      <span className="art alibi" />
    </div>
  );
}

function Welcome({ onHome }: { onHome: () => void }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  return (
    <>
      <Header onHome={onHome} />
      <main id="main" className="welcome">
        <CoverFan />
        <form
          className="welcome-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!saveName(name))
              setError("Use 1–24 characters, without symbols like line breaks.");
          }}
        >
          <h1>What should we call you?</h1>
          <TextField
            label="Your name"
            hideLabel
            placeholder="Your name"
            value={name}
            maxLength={NAME_MAX}
            autoComplete="nickname"
            autoFocus
            error={error}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
          />
          <button className="primary full" disabled={!name.trim()}>
            Let’s play <Icon name="arrow" />
          </button>
        </form>
      </main>
    </>
  );
}

function NameSheet({
  current,
  onClose,
  title = "Your name",
  onSave,
  busy = false,
}: {
  current: string;
  onClose: () => void;
  title?: string;
  onSave?: (name: string) => void;
  busy?: boolean;
}) {
  const [name, setName] = useState(current);
  const [error, setError] = useState("");
  const preview = cleanName(name);
  return (
    <Dialog title={title} onClose={onClose} className="name-sheet">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          const clean = cleanName(name);
          if (!clean) {
            setError("Use 1–24 characters, without symbols like line breaks.");
            return;
          }
          saveName(clean);
          if (onSave) onSave(clean);
          else onClose();
        }}
      >
        <div className="name-preview" aria-hidden="true">
          <Avatar name={preview ?? "?"} index={0} size={56} />
          <span>{preview ?? "Your name"}</span>
        </div>
        <TextField
          label={title === "Your name" ? "Your name" : "Name"}
          value={name}
          maxLength={NAME_MAX}
          autoComplete="nickname"
          autoFocus
          required
          error={error}
          onChange={(e) => {
            setName(e.target.value);
            setError("");
          }}
        />
        <div className="sheet-footer">
          <button className="primary full" disabled={busy || !name.trim()}>
            Save name <Icon name="check" />
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function PlayingAs({
  name,
  onChange,
}: {
  name: string;
  onChange: () => void;
}) {
  return (
    <div className="playing-as">
      <Avatar name={name} index={0} size={40} />
      <span>
        Playing as <strong>{name}</strong>
      </span>
      <button type="button" className="text-button" onClick={onChange}>
        Change
      </button>
    </div>
  );
}

function GameChips({ game }: { game: GameInfo }) {
  return (
    <div className="chip-row">
      <span className="chip">
        <Icon name="user" size={16} />
        {game.players} players
      </span>
      <span className="chip">
        <Icon name="clock" size={16} />
        {game.minutes} min
      </span>
    </div>
  );
}

const filters = [
  ["all", "All games"],
  ["quick", "Quick rounds"],
  ["deduction", "Social deduction"],
  ["two", "For two"],
] as const;

function Catalogue({
  onPlay,
  onRules,
}: {
  onPlay: (g: GameInfo) => void;
  onRules: (g: GameInfo) => void;
}) {
  const [filter, setFilter] = useState("all");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const visible = games.filter(
    (g) =>
      filter === "all" ||
      (filter === "two" && g.min === 2) ||
      (filter === "quick" &&
        (g.id === "impostor" || g.id === "alibi" || g.id === "spectrum")) ||
      (filter === "deduction" && g.category === "deduction"),
  );
  return (
    <main id="main" className="catalogue">
      <h1 className="sr-only">Games</h1>
      <ResumeCards />
      <div className="catalogue-controls">
        <form
          className="join-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/.test(code)) {
              setError("Enter the six-character room code.");
              return;
            }
            navigate(`/r/${code}`);
          }}
        >
          <label className="sr-only" htmlFor="join-code">
            Room code
          </label>
          <div className={`join-input${error ? " has-error" : ""}`}>
            <span className="join-prefix" aria-hidden="true">
              <Icon name="link" size={18} />
            </span>
            <input
              id="join-code"
              value={code}
              placeholder="Have a code?"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "join-code-error" : undefined}
              onChange={(e) => {
                setCode(
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z2-9]/g, "")
                    .slice(0, 6),
                );
                setError("");
              }}
              autoComplete="off"
              autoCapitalize="characters"
              maxLength={6}
              spellCheck={false}
            />
            <button className="join-submit">
              Join <Icon name="arrow" size={16} />
            </button>
          </div>
          {error ? (
            <p className="error" role="alert" id="join-code-error">
              {error}
            </p>
          ) : null}
        </form>
        <div className="filters" role="group" aria-label="Filter games">
          {filters.map(([id, label]) => (
            <button
              className={filter === id ? "active" : ""}
              aria-pressed={filter === id}
              onClick={() => setFilter(id)}
              key={id}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="game-grid">
        {visible.map((g, i) => (
          <article
            className={`game-tile ${g.id}`}
            data-game={g.id}
            key={g.id}
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <button
              className={`art ${g.id}`}
              aria-label={`Play ${g.name}`}
              onClick={() => onPlay(g)}
            />
            <div className="tile-body">
              <div className="tile-head">
                <h2>{g.name}</h2>
                <button
                  className="play-button"
                  aria-label={`Play ${g.name}`}
                  onClick={() => onPlay(g)}
                >
                  Play <Icon name="arrow" size={18} />
                </button>
              </div>
              <div className="tile-meta">
                <span className="chip">{g.players} players</span>
                <span className="chip">{g.minutes} min</span>
                <button className="text-button tile-rules" onClick={() => onRules(g)}>
                  How to play
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}

function Create({
  game,
  savedName,
  onChangeName,
  onClose,
  onCreated,
}: {
  game: GameInfo;
  savedName: string | null;
  onChangeName: () => void;
  onClose: () => void;
  onCreated: (s: SeatCredentials) => void;
}) {
  const [typed, setTyped] = useState("");
  const name = savedName ?? typed;
  const [settings, setSettings] = useState<Obj>(defaults(game));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      ensureSeatStorage();
      const clean = { ...settings };
      if (clean.mafiaCount !== "auto" && clean.mafiaCount !== undefined)
        clean.mafiaCount = Number(clean.mafiaCount);
      const seat = await api<SeatCredentials>("/api/rooms", {
        name: name.trim(),
        gameId: game.id,
        settings: clean,
      });
      if (!savedName) saveName(name);
      onCreated(seat);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={game.name}
      label={`Create a ${game.name} room`}
      onClose={onClose}
      game={game.id}
      className="create-sheet"
      banner={<div className={`sheet-art art ${game.id}`} aria-hidden="true" />}
    >
      <form onSubmit={submit} className="stack create-form">
        <GameChips game={game} />
        {savedName ? (
          <PlayingAs name={savedName} onChange={onChangeName} />
        ) : (
          <TextField
            label="Your name"
            required
            maxLength={NAME_MAX}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="nickname"
            autoFocus
          />
        )}
        <Settings game={game} settings={settings} onChange={setSettings} />
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="sheet-footer">
          <button className="primary full" disabled={busy || !name.trim()}>
            {busy ? "Creating…" : "Create room"} <Icon name="arrow" />
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function settingsSummary(game: GameInfo, s: Obj): string[] {
  const out: string[] = [];
  if (game.id === "impostor") {
    out.push(s.variant === "related" ? "Related word" : "Blank card");
    if (Number(s.cluePasses) > 1) out.push(`${s.cluePasses} clue passes`);
  }
  if (game.id === "mafia") {
    out.push(
      s.mafiaCount === "auto" || s.mafiaCount === undefined
        ? "Mafia: auto"
        : `${s.mafiaCount} mafia`,
    );
    const roles = [s.detective && "Detective", s.doctor && "Doctor"].filter(
      Boolean,
    );
    out.push(roles.length ? roles.join(" + ") : "No special roles");
  }
  if (game.id === "liar-dice") {
    const dice = Number(s.startingDice ?? 5);
    out.push(`${dice} ${dice === 1 ? "die" : "dice"} each`);
    if (s.onesWild) out.push("Ones wild");
  }
  if (game.id === "alibi") {
    out.push(s.conversationMode === "text" ? "Text questions" : "Spoken");
    out.push(`${s.candidateCount ?? 8} locations`);
  }
  if (game.id === "spectrum") {
    out.push(
      s.rounds === "everyone" || s.rounds === undefined
        ? "Everyone guides"
        : `${s.rounds} rounds`,
    );
    if (s.targetMode === "inner-scale") out.push("Inner scale");
  }
  out.push(s.timerMode === "guided" ? "Guided pace" : "Relaxed pace");
  if (s.packId === "custom") out.push("Custom pack");
  return out;
}

const plural = (n: number, one = "player", many = "players") =>
  `${n} ${n === 1 ? one : many}`;

function Room({
  code,
  isDisplay,
  home,
  onRules,
  savedName,
  onChangeName,
}: {
  code: string;
  isDisplay: boolean;
  home: () => void;
  onRules: (g: GameInfo) => void;
  savedName: string | null;
  onChangeName: () => void;
}) {
  const [seat, setSeat] = useState(() => readSeat(code));
  const [preview, setPreview] = useState<Obj | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{
    kind: "kick" | "host";
    id: string;
    name: string;
  } | null>(null);
  const {
    state,
    status,
    error: connectionError,
    send,
    retry,
    pending,
  } = useRoom(code, seat, isDisplay);
  const knownGame: string | undefined = state?.gameId ?? preview?.gameId;
  const g = gameById(knownGame ?? "impostor");
  const isHost = state?.hostId === state?.playerId && !isDisplay;
  const disabled = status !== "online" || pending;
  const [editSettings, setEditSettings] = useState<Obj>({});
  const [leaving, setLeaving] = useState(false);
  const [seatGrid, seatColumns] = useGridColumns();
  const notFound = Boolean(previewError) && !state;
  const { toasts, push: toast, dismiss } = useToasts();
  const [hostBanner, setHostBanner] = useState(false);
  const [changedChips, setChangedChips] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const lastState = useRef<RoomView | null>(null);
  const lastHostName = useRef("The host");
  const requestedName = useRef<{ name: string; from: string | null } | null>(
    null,
  );
  const removedByMe = useRef(new Set<string>());
  const playing = state?.status === "playing";
  const displayChrome = isDisplay && Boolean(state) && state?.status !== "lobby";
  const pageVisible = usePageVisible();
  // Only count disconnection the player can see: a return from another app gets a
  // fresh 1.5s grace before the banner, so quick glances never flash it.
  const showConnection = useDelayedFlag(
    Boolean(state) && status !== "online" && pageVisible,
    1500,
  );
  useWakeLock(playing);
  useIdleCursor(displayChrome);
  useEffect(() => {
    const root = document.documentElement;
    if (knownGame && !notFound) root.dataset.game = knownGame;
    else delete root.dataset.game;
    syncThemeColor();
    return () => {
      delete root.dataset.game;
      syncThemeColor();
    };
  }, [knownGame, notFound]);
  useEffect(() => {
    if (leaving && status === "expired") {
      forgetSeat(code);
      home();
    } else if (status === "removed" || (status === "expired" && seat)) {
      // The credential can never work again; don't offer it on the Resume card.
      forgetSeat(code);
    }
  }, [leaving, status, code]);
  useEffect(() => {
    if (seat || isDisplay) return;
    let active = true;
    api(`/api/rooms/${code}`)
      .then((p) => {
        if (active) setPreview(p);
      })
      .catch((e) => {
        if (active) setPreviewError(e.message);
      });
    return () => {
      active = false;
    };
  }, [code, Boolean(seat), isDisplay]);
  /* Announcements come from diffing consecutive public room views. */
  useEffect(() => {
    if (!state) return;
    const prev = lastState.current;
    lastState.current = state;
    const hostName = state.players.find((p) => p.id === state.hostId)?.name;
    if (hostName) lastHostName.current = hostName;
    const me = state.players.find((p) => p.id === state.playerId);
    const asked = requestedName.current;
    // Held until the lobby/result so it never interrupts an active match.
    if (asked && me && me.name !== asked.from && state.status !== "playing") {
      requestedName.current = null;
      if (renamedOnJoin(asked.name, me.name))
        toast(`Someone’s already ${asked.name} — you’re “${me.name}”.`, {
          label: "Rename",
          onClick: () => setModal("rename"),
        });
    }
    if (!prev || prev.revision === state.revision) return;
    for (const a of announcements(prev, state, (id) => gameById(id).name, {
      removedByMe: removedByMe.current,
    })) {
      if (a.kind === "you-host") setHostBanner(true);
      toast(a.text);
    }
    if (
      state.status === "lobby" &&
      prev.status !== "playing" &&
      state.hostId !== state.playerId
    ) {
      const before = new Set(
        settingsSummary(gameById(prev.gameId), prev.settings),
      );
      const fresh = settingsSummary(gameById(state.gameId), state.settings).filter(
        (c) => !before.has(c),
      );
      if (fresh.length) setChangedChips(new Set(fresh));
    }
  }, [state]);
  useEffect(() => {
    if (!changedChips.size) return;
    const t = setTimeout(() => setChangedChips(new Set()), 3000);
    return () => clearTimeout(t);
  }, [changedChips]);
  useEffect(() => {
    if (!isHost) setHostBanner(false);
  }, [isHost]);
  const command = (a: Action) => {
    send(a);
    setModal(null);
  };
  function edit() {
    if (!state) return;
    setEditSettings(
      editorSettings(
        defaults(g),
        state.settings,
        state.privateRoom.editableCustomPack,
      ),
    );
    setModal("settings");
  }
  /** One tap on a game saves it (and, from a result, returns everyone to the lobby). */
  function chooseGame(id: string) {
    if (!state) return;
    if (id === state.gameId && state.status === "lobby") {
      setModal(null);
      return;
    }
    command({ type: "room.configure", gameId: id });
  }
  const joinName = savedName ?? typed;
  async function join(spectate = false) {
    setBusy(true);
    setError("");
    try {
      ensureSeatStorage();
      const next = await api<SeatCredentials>(
        `/api/rooms/${code}/${spectate ? "spectate" : "join"}`,
        { name: joinName.trim() },
      );
      saveSeat(code, next);
      if (!savedName) saveName(joinName);
      requestedName.current = { name: joinName.trim(), from: null };
      setSeat(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  /** The wordmark mid-match asks first: leaving keeps the seat but the table waits. */
  function goHome() {
    if (playing && !isDisplay && !state?.isSpectator) setModal("leave-table");
    else home();
  }
  const lobby = state?.status === "lobby";
  const players = state?.players.filter((p) => !p.isSpectator) ?? [];
  const me = state?.players.find((p) => p.id === state.playerId);
  const hostName =
    state?.players.find((p) => p.id === state.hostId)?.name ??
    lastHostName.current;
  const openMenu = () => setModal("menu");
  const lobbyInfo = state
    ? lobbyStatus({
        players,
        hostId: state.hostId,
        viewerId: isDisplay ? null : state.playerId,
        isSpectator: state.isSpectator,
        min: g.min,
        max: g.max,
        gameName: g.name,
      })
    : null;
  const fewPlayers = players.length < g.min;
  const offlineToRemove =
    isHost && lobby
      ? lobbyInfo?.offline.find((p) => p.id !== state?.playerId)
      : undefined;
  /* Invite slots only complete the current row of the seat grid, so the grid never
     ends in a lone orphan card; the action bar always offers Invite as well. */
  const emptySeats = Math.min(
    Math.max(0, g.max - players.length),
    (seatColumns - (players.length % seatColumns)) % seatColumns,
  );
  const previewFull =
    preview &&
    (preview.count >= preview.capacity || preview.admissionLocked === true);
  const previewHost =
    typeof preview?.hostName === "string" ? preview.hostName : null;
  const pause = state ? pauseCopy(state.pauseReason, hostName, isHost) : null;
  const resume = () =>
    send({
      type: "room.resume",
      ...(pause?.timeUp ? { extensionSeconds: 30 } : {}),
    });
  return (
    <>
      <Header
        onHome={goHome}
        compact={Boolean(state?.game)}
        minimal={displayChrome}
      >
        {displayChrome ? (
          <>
            <span className="display-label">
              <Icon name="monitor" size={16} />
              Public display
            </span>
            <FullscreenButton />
          </>
        ) : state ? (
          <button
            className="room-code-button"
            onClick={() => setModal("invite")}
            aria-label={`Room ${code}. Invite friends`}
          >
            <span>{code}</span>
            <Icon name="link" size={16} />
          </button>
        ) : null}
        {state && !isDisplay ? (
          <button
            className="icon-button"
            aria-label="Room menu"
            onClick={openMenu}
          >
            <Icon name="menu" />
          </button>
        ) : null}
      </Header>
      <main
        id="main"
        className={`room-page ${g.id}${state?.game ? " is-playing" : ""}${lobby ? " is-lobby" : ""}${lobby && !isDisplay ? " has-lobby-bar" : ""}${displayChrome ? " is-display" : ""}`}
      >
        {!state ? (
          seat || isDisplay ? (
            <div className="center-card status-card">
              <span
                className={`loading-emblem${["removed", "expired", "seat-moved", "error"].includes(status) ? " is-still" : ""}`}
                aria-hidden="true"
              >
                ✳
              </span>
              <h1>
                {status === "removed"
                  ? `${lastHostName.current} removed you from this room`
                  : status === "seat-moved"
                    ? "Your seat is open elsewhere."
                    : status === "expired"
                      ? "This room has closed."
                      : "Connecting…"}
              </h1>
              {status === "removed" ? (
                <p className="muted" role="status">
                  You can join again if they send you the invite.
                </p>
              ) : connectionError ? (
                <p className="muted" role="status">
                  {connectionError}
                </p>
              ) : null}
              {status === "removed" || status === "expired" ? (
                <div className="button-row">
                  <button className="primary" onClick={home}>
                    Back to games
                  </button>
                </div>
              ) : ["error", "offline", "seat-moved"].includes(status) ? (
                <div className="button-row">
                  <button className="primary" onClick={retry}>
                    {status === "seat-moved" ? "Use this tab" : "Try again"}
                  </button>
                  <button className="secondary" onClick={home}>
                    Back to games
                  </button>
                </div>
              ) : null}
            </div>
          ) : previewError ? (
            <NotFound code={code} message={previewError} home={home} />
          ) : (
            <section className="join-room">
              {preview ? (
                <div className="join-card">
                  <div className={`join-art art ${g.id}`} aria-hidden="true" />
                  <div className="join-body">
                    <span className="chip">
                      {previewHost ? `${previewHost}’s room` : "Room"} · {code}
                    </span>
                    <h1>{g.name}</h1>
                    {preview.status === "lobby" && previewFull ? (
                      <div className="join-full" role="status">
                        <h2>
                          {preview.admissionLocked
                            ? "This room is locked"
                            : "This room is full"}
                        </h2>
                        <p>
                          Ask {previewHost ?? "the host"} to{" "}
                          {preview.admissionLocked
                            ? "unlock it"
                            : "free a seat"}
                          , or watch the table for now.
                        </p>
                      </div>
                    ) : (
                      <p className="join-status">
                        {preview.status === "lobby"
                          ? `${preview.count} of ${preview.capacity} in the lobby`
                          : "Game in progress — you can watch and join the next one"}
                      </p>
                    )}
                    <form
                      className="stack"
                      onSubmit={(e) => {
                        e.preventDefault();
                        join(preview.status !== "lobby" || Boolean(previewFull));
                      }}
                    >
                      {savedName ? (
                        <PlayingAs name={savedName} onChange={onChangeName} />
                      ) : (
                        <TextField
                          label="Your name"
                          value={typed}
                          maxLength={NAME_MAX}
                          required
                          autoComplete="nickname"
                          autoFocus
                          onChange={(e) => setTyped(e.target.value)}
                        />
                      )}
                      {error ? (
                        <p className="error" role="alert">
                          {error}
                        </p>
                      ) : null}
                      {preview.status === "lobby" && previewFull ? (
                        <>
                          <button
                            className="primary full"
                            disabled={busy || !joinName.trim()}
                          >
                            <Icon name="eye" />
                            {busy ? "Joining…" : "Watch the table"}
                          </button>
                          <button
                            type="button"
                            className="secondary full"
                            disabled
                          >
                            {preview.admissionLocked
                              ? "Joining is locked"
                              : "No seats left"}
                          </button>
                        </>
                      ) : (
                        <button
                          className="primary full"
                          disabled={busy || !joinName.trim()}
                        >
                          {busy
                            ? "Joining…"
                            : preview.status !== "lobby"
                              ? "Watch & join next game"
                              : savedName
                                ? `Join as ${savedName}`
                                : "Join the room"}
                          <Icon name="arrow" />
                        </button>
                      )}
                    </form>
                  </div>
                </div>
              ) : (
                <div className="center-card status-card">
                  <span className="loading-emblem" aria-hidden="true">
                    ✳
                  </span>
                  <p className="muted" role="status">
                    Loading room…
                  </p>
                </div>
              )}
            </section>
          )
        ) : (
          <>
            {status === "seat-moved" || showConnection ? (
              <div className="connection-banner banner" role="status">
                <span className="banner-icon" aria-hidden="true">
                  <Icon name="link" size={18} />
                </span>
                <span className="banner-text">
                  {status === "seat-moved"
                    ? "Your seat moved to another tab."
                    : (connectionError ?? "Reconnecting. Your place is saved.")}
                </span>
                <button className="text-button" onClick={retry}>
                  {status === "seat-moved" ? "Use this tab" : "Reconnect"}
                </button>
              </div>
            ) : null}
            {hostBanner && isHost ? (
              <div className="host-banner banner">
                <span className="banner-icon" aria-hidden="true">
                  <Icon name="crown" size={18} />
                </span>
                <span className="banner-text">
                  <strong>You’re the host now</strong>
                  <span>You start games, change settings and can pause.</span>
                </span>
                <button
                  className="text-button"
                  onClick={() => setHostBanner(false)}
                >
                  Got it
                </button>
              </div>
            ) : null}
            {state.isSpectator && lobby ? (
              <div className="observer-banner banner">
                <span className="banner-icon" aria-hidden="true">
                  <Icon name="eye" size={18} />
                </span>
                <span className="banner-text">You’re watching.</span>
                <button
                  className="text-button"
                  disabled={disabled}
                  onClick={() => send({ type: "room.joinNextGame" })}
                >
                  Join this game →
                </button>
              </div>
            ) : null}
            {lobby ? (
              <section
                className={`lobby${players.length > 1 ? " has-company" : ""}`}
              >
                <div className="lobby-hero">
                  <div className={`lobby-art art ${g.id}`} aria-hidden="true" />
                </div>
                <div className="lobby-title">
                  <div className="lobby-heading">
                    <h1>{g.name}</h1>
                    <button className="text-button" onClick={() => onRules(g)}>
                      <Icon name="help" size={18} />
                      How to play
                    </button>
                  </div>
                  <GameChips game={g} />
                </div>
                {isDisplay ? <LobbyDisplayInvite code={code} /> : null}
                <div className="lobby-section">
                  <h2 className="section-title">
                    Players
                    <span className="section-count">
                      {players.length} of {g.max}
                    </span>
                  </h2>
                  <div
                    className={`seats${players.length > 6 ? " is-dense" : ""}`}
                    ref={seatGrid}
                  >
                    {players.map((p, i) => {
                      const host = p.id === state.hostId;
                      return (
                        <div
                          className={`seat ${p.connected ? "" : "disconnected"}${p.id === state.playerId ? " is-me" : ""}`}
                          key={p.id}
                        >
                          <Avatar
                            name={p.name}
                            index={i}
                            ready={p.ready && !host}
                            host={host}
                          />
                          <span className="seat-text">
                            <strong className="seat-name">
                              {p.name}
                              {p.id === state.playerId ? (
                                <span className="you-label">You</span>
                              ) : null}
                            </strong>
                            <span
                              className={`seat-status ${!p.connected ? "is-away" : host ? "is-host" : p.ready ? "is-ready" : ""}`}
                            >
                              {!p.connected
                                ? "Offline"
                                : host
                                  ? "Host"
                                  : p.ready
                                    ? "Ready"
                                    : "Not ready"}
                            </span>
                            {players.filter((x) => x.name === p.name).length >
                            1 ? (
                              <small>Seat {i + 1}</small>
                            ) : null}
                          </span>
                        </div>
                      );
                    })}
                    {Array.from({ length: emptySeats }, (_, i) => (
                      <button
                        className="seat empty-seat"
                        onClick={() => setModal("invite")}
                        aria-label="Invite a friend"
                        key={`empty-${i}`}
                      >
                        <span className="empty-avatar">
                          <Icon name="plus" size={22} />
                        </span>
                        <span className="seat-text">
                          <strong className="seat-name">Invite</strong>
                          <span className="seat-status is-open">Open seat</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <TonightCard state={state} />
                <div className="lobby-section">
                  <h2 className="section-title">Settings</h2>
                  {isHost ? (
                    <button
                      className="settings-summary"
                      disabled={disabled}
                      onClick={edit}
                    >
                      <SettingsChips game={g} state={state} changed={changedChips} />
                      <span className="settings-edit">
                        <Icon name="pencil" size={16} /> Edit settings
                      </span>
                    </button>
                  ) : (
                    <div className="settings-summary is-static">
                      <SettingsChips game={g} state={state} changed={changedChips} />
                    </div>
                  )}
                </div>
                {isDisplay ? (
                  <p className="status-note display-status" role="status">
                    {lobbyInfo?.text}
                  </p>
                ) : (
                  <div className="lobby-bar">
                    <p className="status-note" role="status" id="lobby-status">
                      <span>{lobbyInfo?.text}</span>
                      {offlineToRemove ? (
                        <button
                          type="button"
                          className="text-button status-action"
                          onClick={() =>
                            setConfirm({
                              kind: "kick",
                              id: offlineToRemove.id,
                              name: offlineToRemove.name,
                            })
                          }
                        >
                          Remove {offlineToRemove.name}
                        </button>
                      ) : null}
                    </p>
                    <div className="lobby-actions">
                      <button
                        className={`${fewPlayers ? "primary" : "secondary"} invite-button${isHost && !fewPlayers ? " is-compact" : ""}`}
                        onClick={() => setModal("invite")}
                        aria-label="Invite"
                      >
                        <Icon name="link" size={20} />
                        <span className="invite-label">Invite</span>
                      </button>
                      {!state.isSpectator && !isHost ? (
                        <button
                          className={
                            me?.ready
                              ? "secondary ready-button is-ready"
                              : fewPlayers
                                ? "secondary ready-toggle"
                                : "primary ready-toggle"
                          }
                          aria-pressed={Boolean(me?.ready)}
                          aria-describedby={
                            me?.ready ? "ready-undo-hint" : undefined
                          }
                          disabled={disabled}
                          onClick={() =>
                            send({ type: "room.ready", ready: !me?.ready })
                          }
                        >
                          {me?.ready ? (
                            <span className="ready-stack">
                              <span className="ready-main">
                                Ready <Icon name="check" size={18} />
                              </span>
                              <span
                                className="ready-undo"
                                id="ready-undo-hint"
                                aria-hidden="true"
                              >
                                Tap to undo
                              </span>
                            </span>
                          ) : (
                            <>
                              I’m ready
                              <Icon name="check" />
                            </>
                          )}
                        </button>
                      ) : null}
                      {isHost ? (
                        <button
                          className="primary start-button"
                          disabled={disabled || !lobbyInfo?.startable}
                          aria-describedby="lobby-status"
                          onClick={() => send({ type: "room.start" })}
                        >
                          Start game <Icon name="arrow" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </section>
            ) : (
              <>
                <div className="play-topline">
                  <button className="text-button" onClick={() => onRules(g)}>
                    {g.name}
                    <Icon name="help" size={16} />
                  </button>
                  {state.isSpectator && !isDisplay ? <span>Watching</span> : null}
                  {state.deadlineAt !== null &&
                  state.automaticAdvanceAt === null ? (
                    <RoomClock
                      deadline={state.deadlineAt}
                      serverNow={state.serverNow}
                    />
                  ) : null}
                </div>
                {state.paused && pause ? (
                  <div className="pause-banner banner" role="status">
                    <span className="banner-icon" aria-hidden="true">
                      <Icon name={pause.timeUp ? "clock" : "pause"} size={18} />
                    </span>
                    <span className="banner-text">
                      <strong>{pause.title}</strong>
                      {pause.detail ? <span>{pause.detail}</span> : null}
                    </span>
                    {isHost ? (
                      <button
                        className="primary"
                        disabled={disabled}
                        onClick={resume}
                      >
                        {pause.timeUp ? "Add 30s" : "Resume"}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {state.game ? (
                  <Play
                    state={{ ...state, game: state.game }}
                    send={send}
                    disabled={disabled || state.paused}
                    pending={pending}
                    error={
                      playing && status === "online" ? connectionError : null
                    }
                    conceal={status !== "online" || state.paused}
                    isDisplay={isDisplay || state.isSpectator}
                  />
                ) : null}
                {state.status === "finished" ? (
                  <ResultFooter
                    state={state}
                    isHost={isHost}
                    hostName={hostName}
                    disabled={disabled}
                    error={status === "online" ? connectionError : null}
                    onRematch={() => send({ type: "room.rematch" })}
                    onChangeGame={() => setModal("game")}
                    onLobby={() => send({ type: "room.reset" })}
                  />
                ) : null}
              </>
            )}
            {(error || connectionError) && status === "online" && lobby ? (
              <p className="error room-error" role="alert">
                {error || connectionError}
              </p>
            ) : null}
            {displayChrome ? <DisplayCorner code={code} /> : null}
          </>
        )}
      </main>
      <ToastRegion toasts={displayChrome ? [] : toasts} onDismiss={dismiss} />
      {modal === "invite" ? (
        <Invite
          code={code}
          onClose={() => setModal(null)}
          displayEnabled={state?.displayEnabled ?? false}
          inviter={me?.name ?? savedName}
          gameName={g.name}
        />
      ) : null}
      {modal === "settings" && state ? (
        <Dialog
          title="Game settings"
          onClose={() => setModal(null)}
          game={state.gameId}
          className="settings-sheet"
        >
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              const settings = { ...editSettings };
              if (
                settings.mafiaCount !== "auto" &&
                settings.mafiaCount !== undefined
              )
                settings.mafiaCount = Number(settings.mafiaCount);
              command({
                type: "room.configure",
                settings,
              });
            }}
          >
            <div className="ui-field">
              <span className="ui-label" id="current-game-label">
                Game
              </span>
              <button
                type="button"
                className="list-row game-row"
                aria-label={`Game: ${g.name}. Change game`}
                onClick={() => setModal("game")}
              >
                <span className={`row-art art ${g.id}`} aria-hidden="true" />
                <span className="list-text">
                  <span className="list-label">{g.name}</span>
                  <span className="list-hint">{g.players} players</span>
                </span>
                <span className="row-action">Change</span>
              </button>
            </div>
            <Settings
              key={state.gameId}
              game={g}
              settings={editSettings}
              onChange={setEditSettings}
            />
            {state.settings.packId === "custom" &&
            !state.privateRoom.editableCustomPack ? (
              <p className="field-hint">
                Saving replaces this player’s custom pack unless you upload
                another.
              </p>
            ) : null}
            <div className="sheet-footer">
              <p className="field-hint">
                Everyone sees the change straight away. Ready stays as it is.
              </p>
              <button className="primary full" disabled={disabled}>
                Save settings <Icon name="check" />
              </button>
            </div>
          </form>
        </Dialog>
      ) : null}
      {modal === "game" && state ? (
        <ChangeGame
          value={state.gameId}
          fromResult={state.status === "finished"}
          disabled={disabled}
          onClose={() => setModal(state.status === "lobby" ? "settings" : null)}
          onChoose={chooseGame}
        />
      ) : null}
      {modal === "menu" && state ? (
        <Dialog
          title="Room"
          onClose={() => setModal(null)}
          className="menu-sheet"
        >
          <div className="room-menu">
            <div className="list-group">
              <ListRow
                icon="help"
                label={`How to play ${g.name}`}
                onClick={() => {
                  setModal(null);
                  onRules(g);
                }}
              />
              {lobby && !isDisplay ? (
                <ListRow
                  icon="pencil"
                  label="Change your name"
                  hint={me ? `Currently ${me.name}` : undefined}
                  onClick={() => setModal("rename")}
                />
              ) : null}
              {isHost && lobby ? (
                <ListRow
                  icon="sliders"
                  label="Game settings"
                  hint="Game and rules"
                  onClick={edit}
                />
              ) : null}
              {isHost && state.status === "playing" ? (
                <ListRow
                  icon={state.paused ? "play" : "pause"}
                  label={
                    state.paused
                      ? pause?.timeUp
                        ? "Add 30 seconds"
                        : "Resume game"
                      : "Pause game"
                  }
                  hint={
                    state.paused
                      ? "Pick up where you left off"
                      : "Freeze the game; nobody’s choices change"
                  }
                  disabled={disabled}
                  onClick={() => {
                    if (state.paused) resume();
                    else send({ type: "room.pause" });
                    setModal(null);
                  }}
                />
              ) : null}
            </div>
            {isHost ? (
              <>
                <h3 className="list-heading">Room access</h3>
                <div className="list-group">
                  <SwitchRow
                    icon={<Icon name="lock" />}
                    label="Lock new arrivals"
                    hint="Nobody new can join with the link"
                    checked={state.admissionLocked}
                    disabled={disabled}
                    onChange={(v) =>
                      send({ type: "room.configure", admissionLocked: v })
                    }
                  />
                  <SwitchRow
                    icon={<Icon name="eye" />}
                    label="Allow spectators"
                    hint="Late arrivals can watch the public table"
                    checked={state.spectatorsEnabled}
                    disabled={disabled}
                    onChange={(v) =>
                      send({ type: "room.configure", spectatorsEnabled: v })
                    }
                  />
                  <SwitchRow
                    icon={<Icon name="monitor" />}
                    label="Enable shared display"
                    hint="A TV or laptop shows the public table"
                    checked={state.displayEnabled}
                    disabled={disabled}
                    onChange={(v) =>
                      send({ type: "room.configure", displayEnabled: v })
                    }
                  />
                </div>
                {players.length > 1 ? (
                  <details className="list-group manage-details">
                    <summary className="list-row">
                      <span className="row-icon" aria-hidden="true">
                        <Icon name="users" />
                      </span>
                      <span className="list-text">
                        <span className="list-label">Manage players</span>
                        <span className="list-hint">
                          Hand over host or remove someone
                        </span>
                      </span>
                      <span className="row-chevron" aria-hidden="true">
                        <Icon name="chevron-down" />
                      </span>
                    </summary>
                    <div className="manage-players">
                      {players
                        .filter((p) => p.id !== state.playerId)
                        .map((p) => (
                          <div key={p.id}>
                            <Avatar
                              name={p.name}
                              index={players.indexOf(p)}
                              size={28}
                            />
                            <span className="manage-name">
                              <strong>{p.name}</strong>
                              {!p.connected ? <small>Offline</small> : null}
                            </span>
                            <button
                              className="text-button"
                              aria-label={`Make ${p.name} host`}
                              disabled={!p.connected || disabled}
                              onClick={() =>
                                setConfirm({ kind: "host", id: p.id, name: p.name })
                              }
                            >
                              Make host
                            </button>
                            {lobby ? (
                              <button
                                className="text-button danger-text"
                                aria-label={`Remove ${p.name}`}
                                disabled={disabled}
                                onClick={() =>
                                  setConfirm({ kind: "kick", id: p.id, name: p.name })
                                }
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>
                        ))}
                    </div>
                  </details>
                ) : null}
              </>
            ) : null}
            <div className="list-group">
              {isHost && !lobby ? (
                <ListRow
                  icon="flag"
                  label={
                    state.status === "finished"
                      ? "Return to lobby"
                      : "Abandon game"
                  }
                  hint={
                    state.status === "finished"
                      ? "Same room, same invite link"
                      : "End without a winner; everyone returns to the lobby"
                  }
                  danger
                  onClick={() => setModal("abandon")}
                />
              ) : null}
              <ListRow
                icon="door"
                label={
                  leaving
                    ? "Leaving…"
                    : lobby
                      ? "Leave room"
                      : "Leave this screen"
                }
                hint={
                  lobby
                    ? undefined
                    : "Your seat stays reserved. Rejoin in this browser."
                }
                disabled={(lobby || state.isSpectator) && (disabled || leaving)}
                onClick={() => {
                  if (lobby || state.isSpectator) {
                    if (send({ type: "room.leave" })) {
                      setLeaving(true);
                      setModal(null);
                    }
                    return;
                  }
                  setModal(null);
                  home();
                }}
              />
            </div>
          </div>
        </Dialog>
      ) : null}
      {modal === "rename" ? (
        <NameSheet
          title="Change name"
          current={me?.name ?? savedName ?? ""}
          busy={disabled}
          onClose={() => setModal(null)}
          onSave={(name) => {
            requestedName.current = { name, from: me?.name ?? null };
            command({ type: "room.rename", name });
          }}
        />
      ) : null}
      {modal === "abandon" ? (
        <Dialog
          title={
            state?.status === "finished"
              ? "Back to the lobby?"
              : "Abandon this game?"
          }
          onClose={() => setModal(null)}
        >
          <p className="dialog-copy">
            {state?.status === "finished"
              ? "Keep the same room and invite link."
              : "This ends the current game without a winner and removes its secrets. Everyone returns to the lobby."}
          </p>
          <div className="button-row">
            <button className="secondary" onClick={() => setModal(null)}>
              Keep playing
            </button>
            <button
              className="primary danger"
              disabled={disabled}
              onClick={() =>
                command({ type: "room.reset", confirmAbort: true })
              }
            >
              Return to lobby
            </button>
          </div>
        </Dialog>
      ) : null}
      {modal === "leave-table" ? (
        <Dialog title="Leave the table?" onClose={() => setModal(null)}>
          <p className="dialog-copy">
            Your seat stays saved. While you’re away the table may have to wait
            for you — come back from Resume on the games page.
          </p>
          <div className="button-row">
            <button className="secondary" onClick={() => setModal(null)}>
              Stay
            </button>
            <button
              className="primary"
              onClick={() => {
                setModal(null);
                home();
              }}
            >
              Leave table
            </button>
          </div>
        </Dialog>
      ) : null}
      {confirm ? (
        <Dialog
          title={
            confirm.kind === "kick"
              ? `Remove ${confirm.name}?`
              : `Make ${confirm.name} the host?`
          }
          onClose={() => setConfirm(null)}
          className="confirm-sheet"
        >
          <p className="dialog-copy">
            {confirm.kind === "kick"
              ? `${confirm.name} leaves the room straight away. They can join again with the invite link unless you lock the room.`
              : `${confirm.name} will start games, change settings and can pause. You stay at the table as a player.`}
          </p>
          <div className="button-row">
            <button className="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button
              className={`primary${confirm.kind === "kick" ? " danger" : ""}`}
              disabled={disabled}
              onClick={() => {
                if (confirm.kind === "kick") {
                  removedByMe.current.add(confirm.id);
                  if (
                    send({ type: "room.kick", playerId: confirm.id }) !== null
                  )
                    toast(`You removed ${confirm.name}`);
                } else send({ type: "room.transferHost", playerId: confirm.id });
                setConfirm(null);
                setModal(null);
              }}
            >
              {confirm.kind === "kick"
                ? `Remove ${confirm.name}`
                : `Make ${confirm.name} host`}
            </button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

function SettingsChips({
  game,
  state,
  changed,
}: {
  game: GameInfo;
  state: RoomView;
  changed: ReadonlySet<string>;
}) {
  return (
    <span className="chip-row">
      {settingsSummary(game, state.settings).map((s) => (
        <span className={`chip${changed.has(s) ? " is-changed" : ""}`} key={s}>
          {s}
        </span>
      ))}
    </span>
  );
}

/** Tonight's tally: wins per seat across this room's finished matches. */
function sessionRows(state: RoomView) {
  const wins = state.session?.wins ?? {};
  return state.players
    .map((p, i) => ({ ...p, wins: wins[p.id] ?? 0, order: i }))
    .filter((p) => !p.isSpectator || p.wins > 0)
    .sort((a, b) => b.wins - a.wins || a.order - b.order);
}
function lastGameLine(state: RoomView): string | null {
  const last = state.session?.lastResult;
  if (!last) return null;
  const names = last.winnerIds
    .map((id) =>
      id === state.playerId
        ? "You"
        : state.players.find((p) => p.id === id)?.name,
    )
    .filter((n): n is string => Boolean(n));
  const shown =
    names.length > 4 ? `${names.slice(0, 3).join(", ")} +${names.length - 3}` : names.join(", ");
  return `${gameById(last.gameId).name} · ${last.summary}${shown ? ` (${shown})` : ""}`;
}
function TonightCard({ state }: { state: RoomView }) {
  const session = state.session;
  if (!session?.matches) return null;
  const rows = sessionRows(state);
  const anyWins = rows.some((r) => r.wins > 0);
  const last = lastGameLine(state);
  return (
    <section className="lobby-section" aria-labelledby="tonight-title">
      <h2 className="section-title" id="tonight-title">
        Tonight
        <span className="section-count">
          {plural(session.matches, "game", "games")}
        </span>
      </h2>
      <div className="tonight-card">
        {anyWins ? (
          <ol className="tonight-list">
            {rows.map((r) => (
              <li
                key={r.id}
                className={r.id === state.playerId ? "is-me" : undefined}
              >
                <Avatar
                  name={r.name}
                  index={state.players.findIndex((p) => p.id === r.id)}
                  size={28}
                />
                <span className="tonight-name">
                  {r.name}
                  {r.id === state.playerId ? (
                    <span className="you-label">You</span>
                  ) : null}
                </span>
                <strong
                  className="tonight-wins"
                  aria-label={`${plural(r.wins, "win", "wins")}`}
                >
                  {r.wins}
                </strong>
              </li>
            ))}
          </ol>
        ) : null}
        {last ? (
          <p className="tonight-last">
            <Icon name="trophy" size={16} />
            <span>
              <span className="tonight-last-label">Last game</span> {last}
            </span>
          </p>
        ) : null}
      </div>
    </section>
  );
}

function ResultFooter({
  state,
  isHost,
  hostName,
  disabled,
  error,
  onRematch,
  onChangeGame,
  onLobby,
}: {
  state: RoomView;
  isHost: boolean;
  hostName: string;
  disabled: boolean;
  error: string | null;
  onRematch: () => void;
  onChangeGame: () => void;
  onLobby: () => void;
}) {
  const players = state.players.filter((p) => !p.isSpectator);
  const offline = players.filter((p) => !p.connected);
  const rows = sessionRows(state).filter((r) => !r.isSpectator);
  const anyWins = rows.some((r) => r.wins > 0);
  const tally = anyWins
    ? rows
        .slice(0, 6)
        .map((r) => `${r.id === state.playerId ? "You" : r.name} ${r.wins}`)
        .join(" · ")
    : state.session?.lastResult?.summary ?? null;
  return (
    <section className="result-footer rematch-bar" aria-label="What’s next">
      {tally ? (
        <p className="result-tally">
          <Icon name="trophy" size={16} />
          <span>
            <strong>Tonight</strong> {tally}
          </span>
        </p>
      ) : null}
      {isHost ? (
        <>
          {offline.length ? (
            <p className="status-note" role="status">
              Waiting for {nameList(offline.map((p) => p.name))} to reconnect
            </p>
          ) : null}
          <div className="result-actions">
            <button
              className="secondary"
              disabled={disabled}
              onClick={onChangeGame}
            >
              <Icon name="swap" /> Change game
            </button>
            <button
              className="primary"
              disabled={disabled || offline.length > 0}
              onClick={onRematch}
            >
              Play again <Icon name="arrow" />
            </button>
          </div>
          <button
            className="text-button"
            disabled={disabled}
            onClick={onLobby}
          >
            Back to lobby
          </button>
        </>
      ) : (
        <p className="status-note" role="status">
          {state.isDisplay
            ? `Waiting for ${hostName} to start the next game`
            : `Waiting for ${hostName} — play again or pick another game`}
        </p>
      )}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

/** Corner join card for late arrivals on the public display during play. */
function DisplayCorner({ code }: { code: string }) {
  const url = `${location.origin}/r/${code}`,
    qr = useInviteQr(url);
  return (
    <aside className="display-corner" aria-label={`Join room ${code}`}>
      {qr ? (
        <img src={qr} alt="" width="88" height="88" decoding="async" />
      ) : null}
      <span>
        <small>Join</small>
        <strong>{code}</strong>
      </span>
    </aside>
  );
}

function FullscreenButton() {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const sync = () => setFull(Boolean(document.fullscreenElement));
    sync();
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  if (full || typeof document === "undefined" || !document.fullscreenEnabled)
    return null;
  return (
    <button
      className="secondary fullscreen-button"
      onClick={() => {
        document.documentElement.requestFullscreen?.().catch(() => {});
      }}
    >
      <Icon name="maximize" size={18} /> Full screen
    </button>
  );
}

/** Hide the pointer after 3s without movement (public display during play). */
function useIdleCursor(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const root = document.documentElement;
    let timer: ReturnType<typeof setTimeout>;
    const wake = () => {
      root.classList.remove("cursor-idle");
      clearTimeout(timer);
      timer = setTimeout(() => root.classList.add("cursor-idle"), 3000);
    };
    wake();
    addEventListener("pointermove", wake);
    addEventListener("pointerdown", wake);
    return () => {
      clearTimeout(timer);
      removeEventListener("pointermove", wake);
      removeEventListener("pointerdown", wake);
      root.classList.remove("cursor-idle");
    };
  }, [enabled]);
}

/** Keep the screen awake during a match; re-acquire after the tab returns. */
function useWakeLock(active: boolean) {
  useEffect(() => {
    const wakeLock = (
      navigator as Navigator & {
        wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> };
      }
    ).wakeLock;
    if (!active || !wakeLock) return;
    let lock: { release(): Promise<void> } | null = null;
    let stopped = false;
    let requesting = false;
    const acquire = async () => {
      if (stopped || requesting || document.visibilityState !== "visible")
        return;
      requesting = true;
      try {
        const next = await wakeLock.request("screen");
        if (stopped) next.release().catch(() => {});
        else lock = next;
      } catch {
        /* Unsupported, denied or low battery: the game works without it. */
      } finally {
        requesting = false;
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void acquire();
    };
    void acquire();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisible);
      lock?.release().catch(() => {});
      lock = null;
    };
  }, [active]);
}

function usePageVisible() {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const sync = () => setVisible(document.visibilityState !== "hidden");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);
  return visible;
}
/** True only once `on` has held for `ms` (quick tab switches never flash a banner). */
function useDelayedFlag(on: boolean, ms: number) {
  const [value, setValue] = useState(false);
  useEffect(() => {
    if (!on) {
      setValue(false);
      return;
    }
    const t = setTimeout(() => setValue(true), ms);
    return () => clearTimeout(t);
  }, [on, ms]);
  return value;
}

const RESUME_DISMISSED = "after-hours:resume-dismissed:v1";
function readDismissed(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(RESUME_DISMISSED) ?? "[]");
    return Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}
/** Catalogue cards for rooms this browser still holds a seat in. Shows only the code
 * and game (from the public preview), never a role or word. */
function ResumeCards() {
  const [rooms, setRooms] = useState<
    { code: string; gameId: string; status: string; instance: string }[]
  >([]);
  useEffect(() => {
    let active = true;
    const dismissed = readDismissed();
    const seats = listSeats()
      .filter((s) => !dismissed.includes(s.roomInstanceId))
      .slice(0, 3);
    if (!seats.length) return;
    Promise.all(
      seats.map((s) =>
        api(`/api/rooms/${s.code}`)
          .then((p) => ({
            code: s.code,
            gameId: String(p.gameId),
            status: String(p.status),
            instance: s.roomInstanceId,
          }))
          .catch((e) => {
            if (e?.status === 404) forgetSeat(s.code);
            return null;
          }),
      ),
    ).then((list) => {
      if (active) setRooms(list.filter((r) => r !== null));
    });
    return () => {
      active = false;
    };
  }, []);
  if (!rooms.length) return null;
  return (
    <section className="resume-cards" aria-label="Rooms you’re in">
      {rooms.map((r) => {
        const game = gameById(r.gameId);
        return (
          <div className="resume-card" key={r.code} data-game={game.id}>
            <span className={`resume-art art ${game.id}`} aria-hidden="true" />
            <button
              className="resume-main"
              onClick={() => navigate(`/r/${r.code}`)}
            >
              <span className="resume-label">
                Resume {r.code} · {game.name}
              </span>
              <span className="resume-hint">
                {r.status === "lobby" ? "In the lobby" : "Game in progress"} ·
                your seat is saved
              </span>
            </button>
            <button
              className="icon-button"
              aria-label={`Dismiss room ${r.code}`}
              onClick={() => {
                try {
                  localStorage.setItem(
                    RESUME_DISMISSED,
                    JSON.stringify([...readDismissed(), r.instance].slice(-20)),
                  );
                } catch {
                  /* Hidden for this visit only. */
                }
                setRooms((list) => list.filter((x) => x.code !== r.code));
              }}
            >
              <Icon name="close" />
            </button>
          </div>
        );
      })}
    </section>
  );
}

/** Tracks how many columns a CSS grid currently lays out. */
function useGridColumns(): [(el: HTMLDivElement | null) => void, number] {
  const [columns, setColumns] = useState(4);
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const tracks = getComputedStyle(el)
        .gridTemplateColumns.split(" ")
        .filter(Boolean).length;
      if (tracks > 0) setColumns(tracks);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return [setEl, columns];
}

function ListRow({
  icon,
  label,
  hint,
  onClick,
  disabled,
  danger = false,
}: {
  icon: string;
  label: string;
  hint?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  const id = useId();
  return (
    <button
      className={`list-row${danger ? " is-danger" : ""}`}
      aria-label={label}
      aria-describedby={hint ? id : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="row-icon" aria-hidden="true">
        <Icon name={icon} />
      </span>
      <span className="list-text">
        <span className="list-label">{label}</span>
        {hint ? (
          <span className="list-hint" id={id}>
            {hint}
          </span>
        ) : null}
      </span>
      <span className="row-chevron" aria-hidden="true">
        <Icon name="chevron" />
      </span>
    </button>
  );
}

/** One sheet: tapping a game saves it immediately (from a result, it also returns
 * everyone to the lobby with that game). */
function ChangeGame({
  value,
  fromResult,
  disabled,
  onClose,
  onChoose,
}: {
  value: string;
  fromResult: boolean;
  disabled: boolean;
  onClose: () => void;
  onChoose: (id: string) => void;
}) {
  return (
    <Dialog
      title="Change game"
      onClose={onClose}
      wide
      className="game-sheet"
      game={value}
    >
      <div className="stack">
        <p className="field-hint">
          {fromResult
            ? "Tap a game. Everyone heads back to the lobby with it."
            : "Tap a game to switch straight away. Its settings start from the defaults."}
        </p>
        <GameChooser value={value} onChoose={onChoose} disabled={disabled} />
      </div>
    </Dialog>
  );
}

function NotFound({
  code,
  message,
  home,
}: {
  code: string;
  message: string;
  home: () => void;
}) {
  const [value, setValue] = useState(code);
  const [error, setError] = useState("");
  return (
    <section className="not-found">
      <span className="not-found-mark" aria-hidden="true">
        ✳
      </span>
      <h1>Room not found</h1>
      <p className="muted" role="alert">
        {message} Check the code with your host.
      </p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!/^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/.test(value)) {
            setError("Enter the six-character room code.");
            return;
          }
          if (value === code) location.reload();
          else navigate(`/r/${value}`);
        }}
      >
        <TextField
          label="Room code"
          className="code-field"
          value={value}
          error={error}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={6}
          onChange={(e) => {
            setValue(
              e.target.value
                .toUpperCase()
                .replace(/[^A-Z2-9]/g, "")
                .slice(0, 6),
            );
            setError("");
          }}
        />
        <button className="primary full">
          Try again <Icon name="arrow" />
        </button>
        <button type="button" className="secondary full" onClick={home}>
          Start a new room
        </button>
      </form>
    </section>
  );
}

function RoomClock({
  deadline,
  serverNow,
}: {
  deadline: number;
  serverNow: number;
}) {
  const [offset, setOffset] = useState(() => serverNow - Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setOffset(serverNow - Date.now());
  }, [serverNow]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const seconds = Math.max(0, Math.ceil((deadline - now - offset) / 1000));
  return (
    <time
      className={`room-clock${seconds <= 10 ? " is-low" : ""}`}
      aria-label={`${seconds} seconds remaining`}
    >
      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
    </time>
  );
}
function useInviteQr(url: string) {
  const [qr, setQr] = useState("");
  useEffect(() => {
    let active = true;
    setQr("");
    import("qrcode")
      .then((q) =>
        q.toDataURL(url, {
          width: 256,
          margin: 4,
          color: { dark: "#111111", light: "#FFFFFF" },
          errorCorrectionLevel: "M",
        }),
      )
      .then((data) => {
        if (active) setQr(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [url]);
  return qr;
}
function LobbyDisplayInvite({ code }: { code: string }) {
  const url = `${location.origin}/r/${code}`,
    qr = useInviteQr(url);
  return (
    <section className="lobby-display-invite" aria-label="Join this table">
      <div>
        <p>Join on your own phone</p>
        <strong>{code}</strong>
        <span>{url.replace(/^https?:\/\//, "")}</span>
      </div>
      {qr ? (
        <img
          src={qr}
          alt="Scan to join this room"
          width="192"
          height="192"
          decoding="async"
        />
      ) : (
        <span role="status">Preparing invite…</span>
      )}
    </section>
  );
}
function Invite({
  code,
  onClose,
  displayEnabled,
  inviter,
  gameName,
}: {
  code: string;
  onClose: () => void;
  displayEnabled: boolean;
  inviter: string | null;
  gameName: string;
}) {
  const [url] = useState(() => `${location.origin}/r/${code}`);
  const qr = useInviteQr(url);
  const [copied, setCopied] = useState<"link" | "code" | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(t);
  }, [copied]);
  async function copy(kind: "link" | "code") {
    try {
      await navigator.clipboard.writeText(kind === "link" ? url : code);
      setCopied(kind);
      setError("");
    } catch {
      setError(
        kind === "link" ? "Copy the invite link above." : "Copy the code above.",
      );
    }
  }
  const shareText = `${inviter ? `${inviter} invited you` : "You’re invited"} to play ${gameName}`;
  return (
    <Dialog title="Invite friends" onClose={onClose} className="invite-sheet">
      <div className="invite-content">
        <div className="qr-card">
          {qr ? (
            <img
              className="qr"
              src={qr}
              width={200}
              height={200}
              decoding="async"
              alt={`Scan to join room ${code}`}
            />
          ) : (
            <div className="qr-placeholder" />
          )}
        </div>
        <p className="invite-help">Scan with a phone camera, or share the code</p>
        <strong className="invite-code" aria-label={`Room code ${code}`}>
          {code}
        </strong>
        <input
          className="invite-url"
          aria-label="Invite link"
          value={url}
          readOnly
          onFocus={(e) => e.target.select()}
        />
        <div className="invite-actions">
          <button className="primary full" onClick={() => copy("link")}>
            {copied === "link" ? "Link copied" : "Copy invite link"}
            <Icon name={copied === "link" ? "check" : "copy"} />
          </button>
          <div className="invite-secondary">
            <button className="secondary" onClick={() => copy("code")}>
              <Icon name={copied === "code" ? "check" : "copy"} />
              {copied === "code" ? "Code copied" : "Copy code"}
            </button>
            {typeof navigator.share === "function" ? (
              <button
                className="secondary"
                onClick={() =>
                  navigator
                    .share({ title: "After Hours", text: shareText, url })
                    .catch(() => {})
                }
              >
                <Icon name="share" /> Share
              </button>
            ) : null}
          </div>
        </div>
        <span className="sr-only" role="status">
          {copied ? (copied === "link" ? "Link copied" : "Code copied") : ""}
        </span>
        {displayEnabled ? (
          <a
            className="text-button"
            href={`/r/${code}/display`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="monitor" size={18} /> Open shared display ↗
          </a>
        ) : null}
        {error ? (
          <p className="error" role="status">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
